import { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { makeStyles } from "@material-ui/core/styles";
import { DATE_FORMATS, parse } from "utils/Date";
import styled from "styled-components";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(0.5, 1),
    color: "black",
    opacity: ({ isDisabled }) => (isDisabled ? 0.4 : 1),
    fontWeight: ({ isToday }) => (isToday ? "bold" : "inherit"),
    border: ({ isActiveDay }) => (isActiveDay ? "1px solid black" : "none"),
    borderRadius: ({ isActiveDay }) => (isActiveDay ? "5px" : "0"),
    backgroundColor: ({ isToday }) => {
      if (isToday) {
        return "#EEEEEE";
      }

      return "inherit";
    },
    cursor: "pointer",
    userSelect: "none",
    width: "100%",
    height: "100%",
    outline: "none",
    "&:hover": {
      backgroundColor: ({ hasBox }) => (hasBox ? "transparent" : "#EEEEEE"),
    },
  },
}));

function CalendarDay({
  now,
  day,
  currentMonth,
  onClick,
  treatments,
  orders,
  timeslots,
  activeDay,
}) {
  const display = useMemo(() => day.format("D"), [day]);

  const isActiveDay = useMemo(() => day.isSame(activeDay, "day"), [
    activeDay,
    day,
  ]);

  const handleClick = useCallback(
    () => {
      if (onClick) {
        onClick(day);
      }
    },
    [onClick, day],
  );

  const timeslot = useMemo(
    () =>
      timeslots.find(current => {
        const currentDate = parse(
          current.planned_date_time_from,
          DATE_FORMATS.BUMBAL,
        );
        return currentDate.isSame(day, "d");
      }),
    [timeslots, day],
  );

  const hasTreatment = useMemo(
    () =>
      treatments.findIndex(current => {
        const currentDate = parse(
          current.planned_date_time_from,
          DATE_FORMATS.BUMBAL,
        );
        return currentDate.isSame(day, "d");
      }) > -1,
    [treatments, day],
  );

  const hasProductOrder = useMemo(
    () => {
      const o = orders.filter(order => {
        const delivery = moment.unix(order.deliveryDate);
        return delivery.isSame(day, "day");
      });

      return o.length > 0;
    },
    [orders, day],
  );

  const isForbidden = useMemo(() => day.isAfter(now.clone().add(4, "months")), [
    now,
    day,
  ]);

  const isDisabled = useMemo(
    () => {
      const isWeekend = day.day() === 6 || day.day() === 0;

      return !!(
        day.isBefore(now) ||
        isWeekend ||
        currentMonth !== day.month() ||
        isForbidden
      );
    },
    [day, now, currentMonth, isForbidden],
  );

  const isToday = useMemo(() => now.isSame(day, "day"), [now, day]);

  const hasBox = useMemo(() => !!timeslot || hasProductOrder || hasTreatment, [
    timeslot,
    hasTreatment,
    hasProductOrder,
  ]);

  const classes = useStyles({ isDisabled, isToday, hasBox, isActiveDay });

  const renderBox = useCallback(
    content => {
      if (hasBox) {
        return (
          <BoxContainer>
            {timeslot && (
              <ProgressHeader>
                <ProgressBar percentage={timeslot.percent} />
              </ProgressHeader>
            )}

            <BaseBox>
              <DayText hasTreatment={hasTreatment} hasTimeslot={!!timeslot}>
                {content}
              </DayText>
            </BaseBox>
            {hasProductOrder && <Box3D hasTimeslot={!!timeslot} />}
          </BoxContainer>
        );
      }

      return <span>{content}</span>;
    },
    [hasBox, timeslot, hasProductOrder, hasTreatment],
  );

  return (
    <button type="button" onClick={handleClick} className={classes.container}>
      <VisualContainer hasBox={hasBox}>{renderBox(display)}</VisualContainer>
    </button>
  );
}

const VisualContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100%;
`;

const BoxContainer = styled.div`
  position: relative;
  width: 30px;
  height: 24px;
  display: flex;
`;

const BaseBox = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid #454546;
  border-radius: 5px;
  z-index: 2;
  justify-content: center;
  align-items: center;
  background-color: white;
  display: flex;

  &:hover {
    background-color: rgb(238, 238, 238);
  }
`;

const Box3D = styled.div`
  position: absolute;
  width: 100%;
  height: calc(100% + ${({ hasTimeslot }) => (hasTimeslot ? 3 : 0)}px);
  border: 1px solid #454546;
  border-radius: 5px;
  z-index: 1;
  right: -3px;
  top: ${({ hasTimeslot }) => (hasTimeslot ? -5 : -3)}px;
`;

const ProgressHeader = styled.div`
  height: 7px;
  position: absolute;
  background-color: #1d845f;
  width: 100%;
  top: -3px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  left: 0;
  z-index: 3;
  border: 1px solid #454546;
  overflow: hidden;
`;

const ProgressBar = styled.div`
  width: ${({ percentage }) => percentage}%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  background-color: #e68058;
`;

const DayText = styled.span`
  font-weight: bold;
  color: ${({ hasTreatment, hasTimeslot }) => {
    if (hasTimeslot) {
      return "#1d845f";
    }

    if (hasTreatment) {
      return "#23667d";
    }

    return "black";
  }};
`;

CalendarDay.propTypes = {
  now: PropTypes.instanceOf(moment),
  day: PropTypes.instanceOf(moment),
  currentMonth: PropTypes.number,
  onClick: PropTypes.func.isRequired,
  treatments: PropTypes.array,
  orders: PropTypes.array,
  timeslots: PropTypes.array,
  activeDay: PropTypes.instanceOf(moment),
};

export default CalendarDay;
